import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { Input, Select } from '@/components/ui';
import { GlassButton } from '@/design-system';
import type { RateCard } from '@/types';

interface RateCardFormProps {
  initialData?: RateCard | null;
  onSubmit: (data: Partial<RateCard>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  presetLevelId?: string;
  presetYear?: number;
}

const RateCardForm: React.FC<RateCardFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  presetLevelId,
  presetYear,
}) => {
  const { entities: resourceLevels } = useSelector((state: RootState) => state.resourceLevels);

  const [formData, setFormData] = useState({
    resourceLevelId: presetLevelId || '',
    year: presetYear || new Date().getFullYear(),
    hourlyRate: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        resourceLevelId: initialData.resourceLevelId,
        year: initialData.year,
        hourlyRate: initialData.hourlyRate.toString(),
      });
    } else if (presetLevelId || presetYear) {
      setFormData({
        resourceLevelId: presetLevelId || '',
        year: presetYear || new Date().getFullYear(),
        hourlyRate: '',
      });
    }
  }, [initialData, presetLevelId, presetYear]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.resourceLevelId) {
      newErrors.resourceLevelId = 'Resource level is required';
    }

    if (!formData.year) {
      newErrors.year = 'Year is required';
    }

    if (!formData.hourlyRate) {
      newErrors.hourlyRate = 'Hourly rate is required';
    } else {
      const rate = parseFloat(formData.hourlyRate);
      if (isNaN(rate) || rate <= 0) {
        newErrors.hourlyRate = 'Hourly rate must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      resourceLevelId: formData.resourceLevelId,
      year: formData.year,
      hourlyRate: parseFloat(formData.hourlyRate),
      isActive: true,
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const resourceLevelOptions = [
    { value: '', label: 'Select Resource Level...' },
    ...resourceLevels
      .filter(rl => rl.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((level) => ({
        value: level.id,
        label: `${level.displayName} (${level.code})`
      })),
  ];

  const isEditMode = !!initialData;

  // Calculate annual cost preview
  const hourlyRateNum = parseFloat(formData.hourlyRate) || 0;
  const annualCost = hourlyRateNum * 40 * 52;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <Select
          label="Resource Level"
          value={formData.resourceLevelId}
          onChange={(e) => handleInputChange('resourceLevelId', e.target.value)}
          options={resourceLevelOptions}
          error={errors.resourceLevelId}
          required
          fullWidth
          disabled={isEditMode || !!presetLevelId}
          helperText={
            isEditMode || presetLevelId
              ? 'Resource level cannot be changed after creation'
              : 'Select the resource level for this rate'
          }
        />

        <Input
          label="Fiscal Year"
          type="number"
          value={formData.year}
          onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
          error={errors.year}
          placeholder="e.g., 2025"
          required
          fullWidth
          disabled={isEditMode || !!presetYear}
          helperText={
            isEditMode || presetYear
              ? 'Year cannot be changed after creation'
              : 'Enter the fiscal year for this rate'
          }
          min={2020}
          max={2030}
        />

        <div>
          <label className="block text-sm font-medium text-[var(--text)] mb-1.5">
            Hourly Rate <span className="text-status-error">*</span>
          </label>
          <div className="flex items-center">
            <span className="px-3 py-2.5 bg-[var(--surface-muted)] border border-r-0 border-[var(--border)] rounded-l-lg text-[var(--muted)] text-sm">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.hourlyRate}
              onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
              placeholder="0.00"
              className={`flex-1 px-3 py-2.5 bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue ${
                errors.hourlyRate ? 'border-status-error' : ''
              }`}
            />
            <span className="px-3 py-2.5 bg-[var(--surface-muted)] border border-l-0 border-[var(--border)] rounded-r-lg text-[var(--muted)] text-sm">
              /hr
            </span>
          </div>
          {errors.hourlyRate && (
            <p className="mt-1 text-sm text-status-error">{errors.hourlyRate}</p>
          )}
          <p className="mt-1 text-sm text-[var(--muted)]">
            Enter the hourly rate in USD. This will be used for cost calculations.
          </p>
        </div>

        {/* Annual Cost Preview */}
        {hourlyRateNum > 0 && (
          <div className="p-4 rounded-xl bg-accent-emerald/10 border border-accent-emerald/20">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text)]">Annual Cost (FTE)</span>
              <span className="text-lg font-semibold text-accent-emerald">
                ${annualCost.toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-[var(--muted)] mt-1">
              Based on 40 hours/week x 52 weeks
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
        <GlassButton
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </GlassButton>
        <GlassButton
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Rate Card
        </GlassButton>
      </div>
    </form>
  );
};

export default RateCardForm;
